/**
 * Native push notification registration (Capacitor).
 *
 * On a native platform: requests permission, registers with APNs/FCM, sends the
 * device token to the API, and routes deep-links when a push is tapped.
 * No-ops on the web (browser) where Capacitor is not native.
 */
import { Capacitor } from '@capacitor/core';
import { authedFetch } from './api';

let started = false;

export async function initPushNotifications(onDeepLink: (route: string) => void): Promise<void> {
  if (started || !Capacitor.isNativePlatform()) return;
  started = true;

  // Dynamic import so the web bundle never pulls native-only code.
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const platform = Capacitor.getPlatform(); // 'ios' | 'android'

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    await PushNotifications.addListener('registration', (token) => {
      authedFetch('/push/register', {
        method: 'POST',
        body: JSON.stringify({ platform, token: token.value }),
      }).catch(() => { /* will retry next launch */ });
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] registration error', err);
    });

    // Tapped a notification (background/quit) -> deep-link.
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data ?? {};
      const route = typeof data.route === 'string' ? data.route : '/study-groups';
      onDeepLink(route);
    });

    await PushNotifications.register();
  } catch (e) {
    console.warn('[push] init failed', e);
  }
}
